import { type UPDATE_TYPES } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, DialogTitle, IconButton, Stack } from "@mui/material";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { Button } from "antd";
import { isEmpty } from "lodash";
import { useMemo, useState } from "react";

import { useGetProfileChangeLogs } from "./api/useGetProfileChangeLogs";
import { ProfileChangeLogsFilterBar } from "./ProfileChangeLogsFilterBar";
import { ProfileChangeLogsModalContent } from "./ProfileChangeLogsModalContent";
import { buildFilter, isFilterActive } from "./utils/buildFilter";
import { Text } from "@src/appV2/redesign/components/Text";

interface ProfileChangeLogsCardProps {
  id?: string;
}

export function ProfileChangeLogsCard({ id }: ProfileChangeLogsCardProps) {
  const [showLogs, setShowLogs] = useState(false);

  const [emailInput, setEmailInput] = useState("");
  const [contextInput, setContextInput] = useState("");
  const [updatedFieldKeyInput, setUpdatedFieldKeyInput] = useState("");
  const [updateType, setUpdateType] = useState<UPDATE_TYPES | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const debouncedEmail = useDebounce({ value: emailInput });
  const debouncedUpdatedFieldKey = useDebounce({ value: updatedFieldKeyInput });
  const debouncedContext = useDebounce({ value: contextInput });

  const filter = useMemo(
    () =>
      buildFilter({
        // When the input is empty, bypass the debounced value so cleared
        // filters don't trigger an extra request 500ms later when the
        // debounce timer eventually catches up.
        debouncedEmail: isEmpty(emailInput) ? "" : debouncedEmail,
        debouncedUpdatedFieldKey: isEmpty(updatedFieldKeyInput) ? "" : debouncedUpdatedFieldKey,
        debouncedContext: isEmpty(contextInput) ? "" : debouncedContext,
        updateType,
        startDate,
        endDate,
      }),
    [
      emailInput,
      debouncedEmail,
      updatedFieldKeyInput,
      debouncedUpdatedFieldKey,
      contextInput,
      debouncedContext,
      updateType,
      startDate,
      endDate,
    ]
  );

  const { data, isLoading, isFetchingNextPage, isError, fetchNextPage, hasNextPage } =
    useGetProfileChangeLogs({ id: id ?? "", filter }, { enabled: showLogs && isDefined(id) });

  const logs = useMemo(() => data?.pages.flatMap((page) => page.logs) ?? [], [data]);

  const handleClose = () => setShowLogs(false);

  const handleClearFilters = () => {
    setEmailInput("");
    setContextInput("");
    setUpdatedFieldKeyInput("");
    setUpdateType(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
  };

  return (
    <>
      <Button onClick={() => setShowLogs(true)}>Show Profile Change Logs</Button>
      <Dialog
        open={showLogs}
        onClose={handleClose}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: { width: 1200, maxWidth: "100%", height: 800, maxHeight: "90vh" } }}
      >
        <DialogTitle paddingY={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Text>Profile Changes</Text>
            <IconButton aria-label="Close" onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
        >
          <Stack spacing={4} sx={{ flex: 1, minHeight: 0 }}>
            <ProfileChangeLogsFilterBar
              contextInput={contextInput}
              emailInput={emailInput}
              updatedFieldKeyInput={updatedFieldKeyInput}
              updateType={updateType}
              startDate={startDate}
              endDate={endDate}
              isAnyFilterActive={
                isFilterActive(filter) ||
                emailInput.trim().length > 0 ||
                updatedFieldKeyInput.trim().length > 0 ||
                contextInput.trim().length > 0
              }
              onContextInputChange={setContextInput}
              onEmailInputChange={setEmailInput}
              onUpdatedFieldKeyInputChange={setUpdatedFieldKeyInput}
              onUpdateTypeChange={setUpdateType}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onClear={handleClearFilters}
            />
            <ProfileChangeLogsModalContent
              isError={isError}
              isLoading={isLoading}
              isFetchingNextPage={isFetchingNextPage}
              isFiltered={isFilterActive(filter)}
              hasNextPage={hasNextPage ?? false}
              logs={logs}
              onLoadMore={() => void fetchNextPage()}
            />
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
